import { ICity } from '@/shared/types/city';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TMapLoadings, TMapMode, TRouteDirection, TTransportType } from '../types/types';
import { IBusStation } from '@/shared/types/busStaition';
import { IRoadmap, IRoadmapMappoint } from '@/shared/types/roadmap';
import { ITransportVeh } from '@/api/transport/types';
import { IRoute } from '@/api/roadmaps/types';
import { IStopPointShipping } from '@/api/busStaitions/types';

const initCitiesPagination = {
    cities: [],
    rangeOn: 0,
    rangeTo: 20,
    hasMore: true,
    range: 20
}

const initToasts = {
    text: '',
    type: 'loading' as TToastTypeState,
    visible: false,
}

const initialState: StylesState = {
    center: [56.838011, 60.597465],
    zoom: 12,
    bounds: undefined,
    viewPortMode: undefined,
    loadings: [],
    mapMode: 'init',
    cityPagination: initCitiesPagination,
    busStations: [],
    busStationsForm: [],
    busStationsFormTo: [],
    busStationsTo: [],
    busStationsToForm: [],
    busStationsShippingFrom: [],
    busStationsShippingTo: [],
    clikedStaitionByPlacemark: undefined,
    cityRoadmaps: [], // маршруты из города отправления
    roadmaps: [], // маршруты именно для отображения
    formRoadmaps: [], // маршруты, загруженные для выбора в форме, название для понимания
    roadmap: undefined, // выбранный маршрут
    city: undefined, // нас пункт отправление
    cityTo: undefined, // нас пункт прибытия
    citiesInit: [],
    cities: [], // нас пункты отправления
    citiesTo: [], // нас пункты прибытия
    busStation: undefined, // остановка отправления
    busStationTo: undefined, // остановка прибытия
    busStaitionsOnMap: [],
    toast: initToasts,
    isLowFloorTransportEnabled: false,
    vehTransports: [], // весь транспорт на карте 
    transportTypes: ['ТРАМВАЙ', 'АВТОБУС'], // типы транспорта, которые будут отображаться на карте
    //roadmapTransports: [], // транспорт на маршруте 
    transports: [], // айди транспорта на карте
    isTransportsOnMap: false, // флаг есть ли транспорт на карте, отдельный для управления размерами остановок и избежания рендеров
    date: new Date().toString(),
    routeDirection: ['from'], // направление маршрута, откуда или куда
};

const mapScreenSlice = createSlice({
    name: 'mapScreen',
    initialState,
    reducers: {
        controlLoadingMap(state, action: PayloadAction<TMapLoadings>) {
            if (!state.loadings.includes(action.payload)) {
                state.loadings.push(action.payload);
            } else {
                state.loadings = state.loadings.filter((loading) => loading !== action.payload);
            }
        },
        setMapMode(state, action: PayloadAction<TMapMode>) {
            state.mapMode = action.payload
        },
        setCitiesPagination(state, action: PayloadAction<ICityPagination>) {
            state.cityPagination = {
                ...state.cityPagination,
                ...action.payload,
            };
        },
        setToast(state, action: PayloadAction<{ text: string; type: TToastTypeState; visible: boolean }>) {
            state.toast = {
                text: action.payload.text,
                type: action.payload.type,
                visible: action.payload.visible,
            };
        },
        setBusStation(state, action: PayloadAction<IBusStation>) {
            state.busStation = action.payload;
        },
        setBusStationTo(state, action: PayloadAction<IBusStation>) {
            state.busStationTo = action.payload;
        },
        setBusStations(state, action: PayloadAction<IBusStation[]>) {
            state.busStations = action.payload;
        },
        setBusStationsTo(state, action: PayloadAction<IBusStation[]>) {
            state.busStationsTo = action.payload;
        },
        setBusStationsForm(state, action: PayloadAction<IBusStation[]>) {
            state.busStationsForm = action.payload;
        },
        setBusStationsFormTo(state, action: PayloadAction<IBusStation[]>) {
            state.busStationsFormTo = action.payload;
        },
        setBusStationsShippingFrom(state, action: PayloadAction<IStopPointShipping[]>) {
            state.busStationsShippingFrom = action.payload;
        },
        setBusStationsShippingTo(state, action: PayloadAction<IStopPointShipping[]>) {
            state.busStationsShippingTo = action.payload;
        },
        setBusStaitionsOnMap(state, action: PayloadAction<IBusStation[]>) {
            state.busStaitionsOnMap = action.payload;
        },
        setClickedStaitionByPlacemark(state, action: PayloadAction<IBusStation | undefined>) {
            state.clikedStaitionByPlacemark = action.payload;
        },
        setRoadmaps(state, action: PayloadAction<IRoadmapWithPoints[]>) {
            state.roadmaps = action.payload;
        },
        setCityRoadmaps(state, action: PayloadAction<IRoute[]>) {
            state.cityRoadmaps = action.payload;
        },
        setCity(state, action: PayloadAction<ICity>) {
            state.city = action.payload;
        },
        setCityTo(state, action: PayloadAction<ICity>) {
            state.cityTo = action.payload;
        },
        setCitiesInit(state, action: PayloadAction<ICity[]>) {
            state.citiesInit = action.payload;
        },
        setCities(state, action: PayloadAction<ICity[]>) {
            state.cities = action.payload;
        },
        setCitiesTo(state, action: PayloadAction<ICity[]>) {
            state.citiesTo = action.payload;
        },
        setRoadmap(state, action: PayloadAction<IRoadmap>) {
            state.roadmap = action.payload;
        },
        setStateMap(state, action: PayloadAction<number[]>) {
            state.center = action.payload;
        },
        setZoomMap(state, action: PayloadAction<number>) {
            state.zoom = action.payload;
        },
        setBoundsMap(state, action: PayloadAction<IMapBounds>) {
            state.bounds = action.payload;
        },
        setLowFloorTransportEnabled(state, action: PayloadAction<boolean>) {
            state.isLowFloorTransportEnabled = action.payload;
        },
        setVehTransports(state, action: PayloadAction<ITransportVeh[]>) {
            state.vehTransports = action.payload;
            state.isTransportsOnMap = action.payload.length > 0;
        },
        //setRoadmapTransports(state, action: PayloadAction<ITransportVeh[]>) {
        // state.roadmapTransports = action.payload;
        //},
        setTransports(state, action: PayloadAction<number[]>) {
            state.transports = action.payload;
        },
        setViewPortMapMode(state, action: PayloadAction<'centerChange' | 'bounds' | undefined>) {
            state.viewPortMode = action.payload;
        },
        setFormRoadmaps(state, action: PayloadAction<IRoute[]>) {
            state.formRoadmaps = action.payload;
        },
        setDate(state, action: PayloadAction<Date>) {
            state.date = action.payload.toString();
        },
        setTransportTypes(state, action: PayloadAction<TTransportType>) {
            // Устанавливаем типы транспорта, которые будут отображаться на карте
            if (!state.transportTypes.includes(action.payload)) {
                state.transportTypes.push(action.payload);
            } else {
                state.transportTypes = state.transportTypes.filter((type) => type !== action.payload);
            }
        },
        setRouteDirection(state, action: PayloadAction<TRouteDirection>) {
            // Устанавливаем направление маршрута, откуда или куда
            if (!state.routeDirection.includes(action.payload)) {
                state.routeDirection = [action.payload];
            }
        },
    },
});

export const setBusStationAsync = createAsyncThunk(
    'busStation/setAsync',
    async (data: IBusStation, { dispatch }) => {
        dispatch(setBusStation(data)); // Обновляем состояние
        return data; // Возвращаем данные
    }
);

export const {
    setMapMode,
    setCitiesPagination,
    controlLoadingMap,
    setBusStation,
    setClickedStaitionByPlacemark,
    setBusStationTo,
    setBusStations,
    setBusStationsTo,
    setBusStaitionsOnMap,
    setBusStationsForm,
    setBusStationsFormTo,
    setBusStationsShippingFrom,
    setBusStationsShippingTo,
    setRoadmaps,
    setCityRoadmaps,
    setRoadmap,
    setCity,
    setCityTo,
    setCitiesInit,
    setCities,
    setCitiesTo,
    setStateMap,
    setZoomMap,
    setBoundsMap,
    setToast,
    setLowFloorTransportEnabled,
    setVehTransports,
    // setRoadmapTransports,
    setTransports,
    setViewPortMapMode,
    setFormRoadmaps,
    setDate,
    setTransportTypes,
    setRouteDirection,
} = mapScreenSlice.actions;

export default mapScreenSlice.reducer;

interface ICityPagination {
    cities?: ICity[],
    hasMore?: boolean,
    rangeOn?: number,
    rangeTo?: number,
    range?: number
}

export type TToastTypeState = 'loading' | 'info'

interface StylesState {
    center: number[],
    zoom: number,
    bounds: IMapBounds | undefined, // границы карты
    viewPortMode: 'centerChange' | 'bounds' | undefined, // тип перемещения карты 
    loadings: TMapLoadings[],
    mapMode: TMapMode,
    cityPagination: ICityPagination,
    busStations: IBusStation[],
    busStationsForm: IBusStation[], // остановки для формы
    busStationsFormTo: IBusStation[], // остановки для формы
    busStationsShippingFrom: IStopPointShipping[], // остановки откуда можно уехать
    busStationsShippingTo: IStopPointShipping[], // остановки куда можно уехать
    busStationsTo: IBusStation[],
    busStationsToForm: IBusStation[],
    busStaitionsOnMap: IBusStation[],
    city: ICity | undefined, // выбранный город
    cityTo: ICity | undefined, // выбранный город
    citiesInit: ICity[], //  всегда весь список городов
    cities: ICity[], // города отправления
    citiesTo: ICity[], // города прибытия
    busStationTo: IBusStation | undefined, // выбранная остановка транспорта
    busStation: IBusStation | undefined, // выбранная остановка транспорта
    clikedStaitionByPlacemark: IBusStation | undefined, // кликнутая остановка на карте
    roadmaps: IRoadmapWithPoints[] | undefined, // маршруты для отображения на карте
    cityRoadmaps: IRoute[], // маршруты города
    roadmap: IRoadmapWithPoints | undefined, // маршрут выбран на карте(селектед)
    toast: { // управление тостами на карте
        text: string,
        type: TToastTypeState,
        visible: boolean,
    },
    isLowFloorTransportEnabled: boolean, // отображать только низкопольный транспорт
    vehTransports: ITransportVeh[], // транспорт на карте
    transports: number[],
    isTransportsOnMap: boolean, // флаг есть ли транспорт на карте, отдельный для управления размерами остановок и избежания рендеров
    formRoadmaps: IRoute[],
    date: string,
    transportTypes: TTransportType[],
    routeDirection: TRouteDirection[], // направление маршрута, откуда или куда
}

export interface IRoadmapWithPoints extends IRoadmap {
    roadmapPoints?: IRoadmapMappoint[], // точки маршрутаyt необязательны
    veh_type?: 'АВТОБУС' | 'ТРАМВАЙ' | any
}

export interface IMapBounds {
    values: [[number, number], [number, number]]; // Две координаты [lat, lon] для границ карты
    options: {
        checkZoomRange: boolean; // Проверять диапазон зума
        duration: number; // Длительность анимации в миллисекундах
    };
}


